import { useQuery } from '@apollo/react-hooks';
import React, { useEffect } from 'react';
import { Col, Row as FlexRow } from 'react-styled-flexboxgrid';
import DashboardInfoButtons from '../../components/dashboard/DashboardInfoButtons';
import Timeline from '../../components/dashboard/Timeline';
import { Column, Row } from '../../components/Flex';
import useAuthOrRedirect from '../../hooks/useAuthOrRedirect';
import { DASHBOARD } from '../../lib/graphql/queries';
import {
  WHITE_LABEL_ACCENT_COLOR,
  WHITE_LABEL_BRAND,
} from '../../lib/white-label';
import { Media } from '../../theme/media';
import { analytics } from '../../lib/analytics';
import { SalesToLevel } from '../../components/profile/SalesToLevel';
import { PurchasesToNoFee } from '../../components/profile/PurchasesToNoFee';
import { Callout } from '@tremor/react';
import { CashIcon } from '@heroicons/react/solid';
import Link from 'next/link';

const DashboardSkeleton = () => (
  <Column className="min-h-[41vh] flex flex-col gap-y-4 animate-pulse">
    <FlexRow className="mt-6">
      <Col sm={10} md={8} lg={6} className="w-full">
        <h3 className="h-8 bg-gray-200 rounded-md dark:bg-gray-700 w-full"></h3>
      </Col>
      <Col sm={12} md={4} lg={6} className="hidden sm:flex flex-col">
        <h3 className="h-8 bg-gray-200 rounded-md dark:bg-gray-700 w-1/2 ml-auto"></h3>
      </Col>
    </FlexRow>
    <FlexRow className="mt-0 md:mt-4 gap-y-4">
      <Col xs={12} md={4}>
        <h3 className="h-28 bg-gray-200 rounded-md dark:bg-gray-700 w-full"></h3>
      </Col>
      <Col xs={12} md={4}>
        <h3 className="h-28 bg-gray-200 rounded-md dark:bg-gray-700 w-full"></h3>
      </Col>
      <Col xs={12} md={4}>
        <h3 className="h-28 bg-gray-200 rounded-md dark:bg-gray-700 w-full"></h3>
      </Col>
    </FlexRow>
    <FlexRow>
      <Col xs={12} md={8} className="flex flex-col gap-4">
        <h3 className="h-48 bg-gray-200 rounded-md dark:bg-gray-700 w-full"></h3>
        <h3 className="h-48 bg-gray-200 rounded-md dark:bg-gray-700 w-full"></h3>
      </Col>
      <Col xs={12} md={4}>
        <Row className="h-full flex-grow">
          <h3 className="h-full bg-gray-200 rounded-md dark:bg-gray-700 w-full"></h3>
        </Row>
      </Col>
    </FlexRow>
  </Column>
);

const Dashboard = () => {
  const { loading, error, data } = useQuery(DASHBOARD, { ssr: false });
  useEffect(() => {
    analytics.page('Rental Dashboard');
  }, []);
  if (!useAuthOrRedirect()) return null;
  if (loading) return <DashboardSkeleton />;
  if (error) return <p>{error.message}</p>;

  let events = [...data.me.timeline] || [];
  if (!data.me.timeline.length) {
    events = [
      {
        id: '0',
        type: `Welcome, ${data.me.firstName}`,
        message: `Welcome to ${WHITE_LABEL_BRAND}! We hope you enjoy your stay.`,
        createdAt: String(data.me.createdAt),
      },
    ];
  }
  const timeline = <Timeline events={events} />;
  const infoButtons = data.me.isStripeSellerAccountApproved && (
    <DashboardInfoButtons
      sellerPerformance={data.mySalesPerformance}
      // totalMadeCents={data.me.totalMadeCents}
      // availableBalance={data.me.availableBalance}
      // completedPurchases={String(data.myPurchasesCount)}
      // completedRentals={String(data.mySalesCount)}
      // isStripeSellerAccountApproved={data.me.isStripeSellerAccountApproved}
      stripeSellerDashboardSSOLink={data.me.stripeSellerDashboardSSOLink}
    />
  );

  const sellerContent = data.me.isStripeSellerAccountApproved && (
    <>
      <SalesToLevel
        sales={data.mySalesCount}
        sellerLevel={data.me.sellerLevel}
        nextSellerLevel={data.me.nextSellerLevel}
      />
    </>
  );
  const buyerContent = (
    <>
      <PurchasesToNoFee purchasesUntilNoFee={data.me.purchasesUntilNoFee} />
    </>
  );
  return (
    <Column className="min-h-[41vh]">
      <FlexRow>
        <Col sm={10} md={8} lg={6} className="w-full">
          <p className="lg:mb-5 text-2xl md:text-3xl pt-0 md:pt-5 pb-2">
            <span className="font-bold">Hey {data.me.firstName},</span>{' '}
            <span className="font-normal">Welcome Back!</span>
          </p>
        </Col>
        {!data.me.isStripeSellerAccountApproved && (
          <Col sm={12} md={4} className="flex flex-col ml-auto w-full">
            <div>
              <Link href={'/dashboard/settings'}>
                <Callout
                  title="Interested in becoming a seller?"
                  icon={CashIcon}
                  color="green"
                >
                  <span className="font-bold underline">Apply now</span> and get
                  started boosting your revenue today!
                </Callout>
              </Link>
            </div>
          </Col>
        )}
        {data.me.isStripeSellerAccountApproved && (
          <Col sm={12} md={4} lg={6} className="flex flex-col">
            <p className="lg:mb-5 pt-0 md:pt-4 lg:pt-7 pb-2 ml-auto">
              <span className="font-semibold mr-2">Direct Link:</span>
              <Link href={`/bots/seller/${data.me.username}`}>
                <span
                  className="cursor-pointer"
                  style={{ color: WHITE_LABEL_ACCENT_COLOR }}
                >
                  {`${process.env.NEXT_PUBLIC_BASE_URL}/bots/seller/${data.me.username}`}
                </span>
              </Link>
            </p>
          </Col>
        )}
      </FlexRow>
      <Media greaterThanOrEqual="lg">
        <FlexRow className="mb-4">
          <Col xs={12}>{infoButtons}</Col>
        </FlexRow>
        <FlexRow>
          <Col xs={8} className="flex flex-col gap-4">
            {sellerContent && <Row className="h-1/2">{sellerContent}</Row>}
            <Row className={sellerContent ? 'h-1/2' : 'h-full'}>
              {buyerContent}
            </Row>
          </Col>
          <Col xs={4}>
            <Row className="h-full flex-grow">{timeline}</Row>
          </Col>
        </FlexRow>
      </Media>
      <Media lessThan="lg">
        <FlexRow className="mb-4">
          <Col xs={12}>{infoButtons}</Col>
        </FlexRow>
        <FlexRow className="flex flex-col gap-4">
          <Col xs={12} className="flex flex-col gap-4">
            {sellerContent && <Row className="h-1/2">{sellerContent}</Row>}
            <Row className={sellerContent ? 'h-1/2' : 'h-full'}>
              {buyerContent}
            </Row>
          </Col>
          <Col xs={12}>
            <Row>{timeline}</Row>
          </Col>
        </FlexRow>
      </Media>
    </Column>
  );
};

export async function getStaticProps() {
  return { props: {} };
}

export default Dashboard;
